import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import ContourBG from '@components/ContourBG';
import XPLegend from '@components/XPLegend';
import {
  User, Palette, CreditCard, CheckCircle,
  Monitor, Sun, Moon, Trash2, AlertTriangle, Eye, EyeOff, Lock, Zap, ChevronDown,
} from 'lucide-react';
import { supabase } from '@services/api.js';
import { usePageTitle } from '@hooks/usePageTitle';
import { useDarkMode } from '@hooks/useDarkMode';
import { isValidEmail } from '../utils/validation';

// Same variable and fallback as useCopyScoring.js (it already ends in /api).
const API_BASE = (import.meta.env.VITE_APP_API_URL || 'http://localhost:3000/api').replace(/\/+$/, '');

const getStrength = (pw) => {
  if (!pw) return null;
  const strong = pw.length >= 12 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);
  const medium = pw.length >= 8 && (/[A-Z]/.test(pw) || /[0-9]/.test(pw));
  return strong ? 'strong' : medium ? 'medium' : 'weak';
};
const SC = { strong: '#22c55e', medium: '#f59e0b', weak: '#ef4444' };
const SL = { strong: 'Strong', medium: 'Medium', weak: 'Weak' };

/* ── Tiny Toast ── */
const Toast = ({ toasts }) => (
  <div style={{ position:'fixed', bottom:20, right:20, zIndex:9999, display:'flex', flexDirection:'column', gap:6, pointerEvents:'none' }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        display:'flex', alignItems:'center', gap:8,
        padding:'9px 14px', borderRadius:10,
        background: t.type==='success' ? '#052e16' : '#450a0a',
        border:`1px solid ${t.type==='success' ? '#166534' : '#991b1b'}`,
        color: t.type==='success' ? '#86efac' : '#fca5a5',
        fontSize:12, fontWeight:600,
        boxShadow:'0 4px 16px rgba(0,0,0,0.3)',
        animation:'toast-in 0.25s ease-out',
        whiteSpace:'nowrap',
      }}>
        <CheckCircle size={13} />
        {t.text}
      </div>
    ))}
  </div>
);

/* ── Delete Modal ── */
const DeleteModal = ({ onConfirm, onCancel, loading }) => {
  const [confirmText, setConfirmText] = useState('');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:24 }}>
      <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-700 shadow-2xl p-8 max-w-md w-full" style={{ animation:'toast-in 0.2s ease-out' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle size={18} className="text-red-500" />
          </div>
          <h3 className="text-base font-bold text-stone-900 dark:text-white">Delete Account</h3>
        </div>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 leading-relaxed">
          This permanently deletes your account and all progress. <strong>Cannot be undone.</strong>
        </p>
        <p className="text-xs text-stone-400 dark:text-stone-500 mb-3">
          Type <span className="font-mono font-bold text-red-500">DELETE</span> to confirm:
        </p>
        <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
          placeholder="DELETE" className="input mb-4" />
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
          <button onClick={onConfirm} disabled={confirmText !== 'DELETE' || loading}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
              confirmText === 'DELETE' ? 'bg-red-600 hover:bg-red-700 text-white cursor-pointer' : 'bg-stone-100 dark:bg-stone-800 text-stone-400 cursor-not-allowed'
            }`}>
            <Trash2 size={14} />{loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  usePageTitle('Settings');
  const { user, profile, refreshProfile, signOut } = useAuth();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [xpOpen, setXpOpen] = useState(false);
  const [deleteLoading, setDeleteLoading]     = useState(false);
  const [toasts, setToasts]                   = useState([]);
  const [saving, setSaving]                   = useState({});
  const [fullName, setFullName]   = useState(profile?.full_name || '');
  const [newEmail, setNewEmail]   = useState(user?.email || '');
  const [passwords, setPasswords] = useState({ new: '', confirm: '' });
  const [showPw, setShowPw]       = useState({ new: false, confirm: false });

  // Reads the real theme on the first render (was useState(false) fixed up in an
  // effect, which painted light for a frame in dark mode).
  const isDark = useDarkMode();

  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    return localStorage.getItem('theme') || 'system';
  });

  useEffect(() => {
    if (profile) setFullName(profile.full_name || '');
    if (user)    setNewEmail(user.email || '');
  }, [profile, user]);

  const addToast = useCallback((type, text) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, type, text }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);

  const nameTimer = useRef(null);
  const handleNameChange = (val) => {
    setFullName(val);
    clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(async () => {
      if (!val.trim() || val === profile?.full_name) return;
      setSaving(s => ({ ...s, name: true }));
      try {
        await supabase.from('profiles').update({ full_name: val }).eq('id', user.id);
        await refreshProfile();
        addToast('success', 'Name saved');
      } catch { addToast('error', 'Failed to save'); }
      finally { setSaving(s => ({ ...s, name: false })); }
    }, 900);
  };

  const handleUpdateEmail = async () => {
    if (!isValidEmail(newEmail)) { addToast('error', 'Invalid email'); return; }
    if (newEmail === user.email)  { addToast('error', 'Same as current email'); return; }
    setSaving(s => ({ ...s, email: true }));
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      addToast('success', 'Confirmation sent');
    } catch (err) { addToast('error', err.message); }
    finally { setSaving(s => ({ ...s, email: false })); }
  };

  const handleUpdatePassword = async () => {
    if (passwords.new !== passwords.confirm) { addToast('error', "Passwords don't match"); return; }
    if (getStrength(passwords.new) === 'weak') { addToast('error', 'Password too weak'); return; }
    setSaving(s => ({ ...s, pw: true }));
    try {
      const { error } = await supabase.auth.updateUser({ password: passwords.new });
      if (error) throw error;
      addToast('success', 'Password updated');
      setPasswords({ new: '', confirm: '' });
    } catch (err) { addToast('error', err.message); }
    finally { setSaving(s => ({ ...s, pw: false })); }
  };

  const handleThemeChange = (val) => {
    setTheme(val);
    const root = document.documentElement;
    root.classList.add('no-transition');
    if (val === 'dark')       { root.classList.add('dark');    localStorage.setItem('theme', 'dark'); }
    else if (val === 'light') { root.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
    else {
      localStorage.removeItem('theme');
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? root.classList.add('dark') : root.classList.remove('dark');
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => root.classList.remove('no-transition'));
    });
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      // The backend removes every row the user owns and then the auth user
      // (DELETE /api/users/me). Deleting from the browser only ever reached 3
      // tables, left the auth user behind, and is no longer permitted by RLS.
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/users/me`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
      });
      if (!res.ok) throw new Error(`Account deletion failed (${res.status})`);
      await signOut();
    } catch (err) {
      addToast('error', 'Could not delete. Contact support.');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const pwStrength = getStrength(passwords.new);
  const pwMatch    = !!(passwords.confirm && passwords.confirm === passwords.new);

  const c = {
    card:        isDark ? '#1c1917' : '#ffffff',
    cardBorder:  isDark ? '#2d2825' : '#e8e4df',
    inner:       isDark ? '#151211' : '#f5f2ee',
    innerBorder: isDark ? '#2d2825' : '#e8e4df',
    head:        isDark ? '#fafaf9' : '#1c1917',
    muted:       isDark ? '#78716c' : '#a8a29e',
    sub:         isDark ? '#57534e' : '#a8a29e',
    divider:     isDark ? '#2d2825' : '#f0ece7',
    inputBg:     isDark ? '#1c1917' : '#ffffff',
    inputBorder: isDark ? '#2d2825' : '#e8e4df',
  };

  const sCard  = { background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 20, padding: 24 };
  const sLabel = { fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 };

  /* ── Dot grid — same as Dashboard / Skills / Billing ── */
  const dotGrid = isDark
    ? 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)'
    : 'radial-gradient(circle, rgba(0,0,0,0.055) 1px, transparent 1px)';
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';

  return (
    <>
      <style>{`
        @keyframes toast-in { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes se { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .se1 { animation: se 0.3s ease-out both; }
        .se2 { animation: se 0.3s ease-out 0.07s both; }
        .se3 { animation: se 0.3s ease-out 0.14s both; }
        .se4 { animation: se 0.3s ease-out 0.21s both; }

        .settings-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          align-items: start;
        }
        @media (max-width: 700px) {
          .settings-grid { grid-template-columns: 1fr; }
        }

        .pw-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }
        @media (max-width: 500px) {
          .pw-grid { grid-template-columns: 1fr; }
        }

        .theme-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        @media (max-width: 400px) {
          .theme-grid { gap: 6px; }
          .theme-btn  { padding: 10px 4px !important; }
          .theme-label { font-size: 11px !important; }
          .theme-sub   { display: none !important; }
        }
      `}</style>

      <Toast toasts={toasts} />
      {showDeleteModal && <DeleteModal onConfirm={handleDeleteAccount} onCancel={() => setShowDeleteModal(false)} loading={deleteLoading} />}

{/* ── Atlas Plates wrapper ── */}
      <div style={{ minHeight: '100vh', backgroundColor: pageBg }}>

        {/* Header — contour + plate eyebrow */}
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 150, overflow: 'hidden', pointerEvents: 'none' }}>
            <ContourBG isDark={isDark} size="compact" opacity={0.9} />
          </div>
          <div className="se1 rail-clearance" style={{ maxWidth: '100%', padding: '48px 24px 20px', position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>THE OUTFIT</span>
              <span style={{ width: 22, height: 1, background: isDark ? '#57534e' : '#d4cdc7', display: 'inline-block' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: c.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Settings</span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem,4.5vw,2.2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: c.head, margin: '0 0 8px', lineHeight: 1.1 }}>Account &amp; Preferences</h1>
            <p style={{ fontSize: 14, color: c.muted, margin: 0 }}>Manage your profile, security, and display preferences.</p>
          </div>
        </div>

        <div className="rail-clearance" style={{ maxWidth: '100%', padding: '20px 24px 72px' }}>

          {/* Responsive 2-col → 1-col grid */}
          <div className="settings-grid">

            {/* ══ LEFT: Account + Password + Danger ══ */}
            <div className="se2" style={{ ...sCard, display: 'flex', flexDirection: 'column', gap: 0 }}>

              {/* Profile */}
              <div style={{ marginBottom: 24 }}>
                <div style={sLabel}>
                  <User size={13} color={c.muted} /> Profile
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: c.sub, display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span>Full Name</span>
                    {saving.name && <span style={{ color: c.muted }}>Saving...</span>}
                  </label>
                  <input type="text" value={fullName} onChange={e => handleNameChange(e.target.value)}
                    className="input" placeholder="Your name" />
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: c.sub, display: 'block', marginBottom: 6 }}>Email Address</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                      className="input" style={{ flex: 1, minWidth: 0 }} />
                    <button onClick={handleUpdateEmail} disabled={saving.email || newEmail === user?.email}
                      style={{
                        flexShrink: 0, padding: '0 14px', height: 40, borderRadius: 10, border: 'none',
                        fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
                        background: newEmail !== user?.email ? '#ea580c' : c.inner,
                        color: newEmail !== user?.email ? '#fff' : c.muted,
                      }}>
                      {saving.email ? '...' : 'Update'}
                    </button>
                  </div>
                  <p style={{ fontSize: 10, color: c.muted, marginTop: 5 }}>A confirmation link will be sent before email changes.</p>
                </div>
              </div>

              <div style={{ height: 1, background: c.divider, margin: '0 0 24px 0' }} />

              {/* Password */}
              <div style={{ marginBottom: 24 }}>
                <div style={sLabel}>
                  <Lock size={13} color={c.muted} /> Change Password
                </div>

                <div className="pw-grid">
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: c.sub, display: 'block', marginBottom: 6 }}>New Password</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPw.new ? 'text' : 'password'} placeholder="••••••••"
                        value={passwords.new} onChange={e => setPasswords({ ...passwords, new: e.target.value })}
                        className="input" style={{ paddingRight: 36 }} />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, new: !p.new }))}
                        style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color: passwords.new ? SC[pwStrength] : c.muted, transition:'color 0.25s' }}>
                        {showPw.new ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: c.sub }}>Confirm</span>
                      {passwords.new && pwStrength && (
                        <span style={{ color: SC[pwStrength], fontWeight: 800, fontSize: 11 }}>{SL[pwStrength]}</span>
                      )}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPw.confirm ? 'text' : 'password'} placeholder="••••••••"
                        value={passwords.confirm} onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                        className="input" style={{ paddingRight: 36 }} />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))}
                        style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color: pwMatch ? '#22c55e' : c.muted, transition:'color 0.25s' }}>
                        {showPw.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                </div>

                <button onClick={handleUpdatePassword} disabled={saving.pw || !passwords.new || !pwMatch}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 10, border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: passwords.new && pwMatch ? 'pointer' : 'not-allowed',
                    background: passwords.new && pwMatch ? '#ea580c' : c.inner,
                    color: passwords.new && pwMatch ? '#fff' : c.muted,
                    transition: 'all 0.2s',
                  }}>
                  {saving.pw ? 'Updating...' : 'Update password'}
                </button>
              </div>

              <div style={{ height: 1, background: c.divider, margin: '0 0 24px 0' }} />

              {/* Danger zone */}
              <div>
                <div style={{ ...sLabel, color: '#ef4444' }}>
                  <AlertTriangle size={13} color="#ef4444" /> Danger Zone
                </div>
                <div style={{ padding: 14, borderRadius: 12, background: isDark ? 'rgba(239,68,68,0.06)' : '#fff5f5', border: `1px solid ${isDark ? 'rgba(239,68,68,0.18)' : '#fecaca'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: c.head, marginBottom: 2 }}>Delete Account</p>
                    <p style={{ fontSize: 11, color: c.muted }}>Permanently deletes all data. Cannot be undone.</p>
                  </div>
                  <button onClick={() => setShowDeleteModal(true)}
                    style={{ flexShrink: 0, display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:9, border:`1px solid ${isDark ? 'rgba(239,68,68,0.3)' : '#fca5a5'}`, background:'transparent', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>

            </div>

            {/* ══ RIGHT: Appearance + Plan ══ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Appearance */}
              <div className="se3" style={sCard}>
                <div style={sLabel}>
                  <Palette size={13} color={c.muted} /> Appearance
                </div>
                <p style={{ fontSize: 12, color: c.muted, marginBottom: 16 }}>Choose how Castle Atlas looks on your device</p>

                <div className="theme-grid">
                  {[
                    { val: 'light',  icon: Sun,     label: 'Light',  sub: 'Clean white interface' },
                    { val: 'dark',   icon: Moon,    label: 'Dark',   sub: 'Easy on the eyes' },
                    { val: 'system', icon: Monitor, label: 'System', sub: 'Follows your OS' },
                  ].map(({ val, icon: Icon, label, sub }) => {
                    const active = theme === val;
                    return (
                      <button key={val} onClick={() => handleThemeChange(val)}
                        className="theme-btn"
                        style={{
                          padding: '14px 8px', borderRadius: 12, textAlign: 'center',
                          border: `2px solid ${active ? '#ea580c' : c.innerBorder}`,
                          background: active ? (isDark ? 'rgba(234,88,12,0.15)' : '#fff7ed') : c.inner,
                          cursor: 'pointer', transition: 'all 0.2s',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                        }}>
                        <Icon size={18} color={active ? '#ea580c' : c.muted} />
                        <span className="theme-label" style={{ fontSize: 12, fontWeight: 700, color: active ? '#ea580c' : c.head }}>{label}</span>
                        <span className="theme-sub" style={{ fontSize: 10, color: active ? '#ea580c' : c.muted, lineHeight: 1.3, opacity: active ? 0.85 : 0.7 }}>{sub}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Plan */}
              <div className="se4" style={sCard}>
                <div style={sLabel}>
                  <CreditCard size={13} color={c.muted} /> Plan
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: isDark ? 'rgba(234,88,12,0.15)' : '#fff7ed', border: `1px solid ${isDark ? 'rgba(234,88,12,0.3)' : '#fed7aa'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#ea580c' }}>FREE</span>
                    </div>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 700, color: c.head, marginBottom: 2 }}>Free Plan</p>
                      <p style={{ fontSize: 12, color: c.muted }}>AI Prompt Engineering · Phase 1</p>
                    </div>
                  </div>
                  <Link to="/billing" style={{ textDecoration: 'none', flexShrink: 0 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 12, fontWeight: 700, color: c.muted,
                      padding: '6px 12px', borderRadius: 8,
                      border: `1px solid ${c.cardBorder}`,
                      background: c.inner,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ea580c'; e.currentTarget.style.borderColor = '#ea580c'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = c.muted; e.currentTarget.style.borderColor = c.cardBorder; }}>
                      View plans →
                    </span>
                  </Link>
                </div>
              </div>

              {/* XP Legend — collapsible */}
              <div className="se4" style={{ ...sCard, overflow: 'hidden' }}>
                <button
                  onClick={() => setXpOpen(o => !o)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #FF6B35, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Zap size={15} color="#fff" fill="#fff" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: c.head, margin: 0 }}>How to Earn XP</p>
                      <p style={{ fontSize: 11, color: c.muted, margin: 0 }}>Complete actions to level up your rank</p>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    color={c.muted}
                    style={{
                      transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
                      transform: xpOpen ? 'rotate(-180deg)' : 'rotate(0deg)',
                      flexShrink: 0,
                    }}
                  />
                </button>

                <div style={{
                  display: 'grid',
                  gridTemplateRows: xpOpen ? '1fr' : '0fr',
                  transition: 'grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1)',
                }}>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ paddingTop: 20 }}>
                      <XPLegend isDark={isDark} compact showHeader={false} bare />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;